// Pair-endpoint snippet for Godot 4.5 / GDScript. Drop in as an autoload
// singleton; redeems the code the player pasted from the bot DM, stores
// the returned player token to `user://` so it survives restarts, then
// hands it off to the event-firing singleton.

export function buildPairGodotSnippet(apiKey: string, functionsBaseUrl: string): string {
  return `# res://addons/playerbase/player_base_pairing.gd
# One-off pairing handshake. Register as an autoload singleton:
# Project > Project Settings > Autoload > "PlayerBasePairing".
# Godot 4.5 / GDScript.
#
# Flow:
#   1. Player taps the recruitment button in Discord -> bot DMs them a
#      short code.
#   2. Your in-game pairing UI calls PlayerBasePairing.redeem(code).
#   3. On success, pairing_succeeded fires with the playerToken. The
#      token plus the install id are written to user://playerbase.cfg
#      and reused on every subsequent /events call (see PlayerBaseEvents).
#
# The install id is generated once per device and kept across re-pairs
# (unpair() leaves it intact) so the backend can correlate sessions
# from the same install.
extends Node

const API_KEY := "${apiKey}"
const PAIR_URL := "${functionsBaseUrl}/pair"
const STORE_PATH := "user://playerbase.cfg"

signal pairing_succeeded(player_token: String)
signal pairing_failed(reason: String)

var player_token: String = ""
var install_id: String = ""

func _ready() -> void:
\tvar cfg := ConfigFile.new()
\tif cfg.load(STORE_PATH) == OK:
\t\tplayer_token = cfg.get_value("pb", "player_token", "")
\t\tinstall_id = cfg.get_value("pb", "install_id", "")
\tif install_id == "":
\t\tinstall_id = _new_uuid()
\t\t_persist()

func is_paired() -> bool:
\treturn player_token != ""

# Wipe the saved token. Use this for a "Sign out / unpair" affordance in
# settings. The install id is kept so a later re-pair can still be
# correlated with the same install.
func unpair() -> void:
\tplayer_token = ""
\t_persist()

# Call from your in-game pairing UI once the player has pasted the code
# from the Discord DM. Listen to pairing_succeeded / pairing_failed to
# advance the UI.
func redeem(pairing_code: String) -> void:
\tvar body := JSON.stringify({
\t\t"code": pairing_code.strip_edges(),
\t\t"installId": install_id,
\t})
\tvar signature := _sign(body)
\tvar req := HTTPRequest.new()
\tadd_child(req)
\treq.request_completed.connect(_on_pair_completed.bind(req))
\tvar err := req.request(
\t\tPAIR_URL,
\t\tPackedStringArray([
\t\t\t"content-type: application/json",
\t\t\t"x-playerbase-signature: " + signature,
\t\t]),
\t\tHTTPClient.METHOD_POST,
\t\tbody
\t)
\tif err != OK:
\t\treq.queue_free()
\t\tpairing_failed.emit("transport")

func _on_pair_completed(
\t_result: int,
\tresponse_code: int,
\t_headers: PackedStringArray,
\tbody: PackedByteArray,
\treq: HTTPRequest
) -> void:
\treq.queue_free()
\tif response_code != 200:
\t\tvar reason := "http_%d" % response_code
\t\tvar err_text := body.get_string_from_utf8()
\t\tvar err_json: Variant = JSON.parse_string(err_text)
\t\tif err_json is Dictionary and err_json.has("error"):
\t\t\treason = String(err_json["error"])
\t\tpairing_failed.emit(reason)
\t\treturn
\tvar parsed: Variant = JSON.parse_string(body.get_string_from_utf8())
\tif not (parsed is Dictionary) or not parsed.has("playerToken"):
\t\tpairing_failed.emit("bad_response")
\t\treturn
\tplayer_token = String(parsed["playerToken"])
\t_persist()
\tpairing_succeeded.emit(player_token)

func _persist() -> void:
\tvar cfg := ConfigFile.new()
\tcfg.set_value("pb", "player_token", player_token)
\tcfg.set_value("pb", "install_id", install_id)
\tcfg.save(STORE_PATH)

func _sign(body: String) -> String:
\tvar hmac := HMACContext.new()
\thmac.start(HashingContext.HASH_SHA256, API_KEY.to_utf8_buffer())
\thmac.update(body.to_utf8_buffer())
\treturn hmac.finish().hex_encode()

func _new_uuid() -> String:
\tvar b := PackedByteArray()
\tb.resize(16)
\tfor i in 16:
\t\tb[i] = randi() & 0xff
\tb[6] = (b[6] & 0x0f) | 0x40
\tb[8] = (b[8] & 0x3f) | 0x80
\tvar h := b.hex_encode()
\treturn "%s-%s-%s-%s-%s" % [
\t\th.substr(0, 8), h.substr(8, 4), h.substr(12, 4),
\t\th.substr(16, 4), h.substr(20, 12),
\t]
`;
}
